import { PlayCircle } from "lucide-react";
import { useState } from "react";

// Collapsed by default hint that appears under every embedded video. When a parent's
// browser/ad-blocker/privacy extension blocks the Descript iframe, the video area shows
// blank with no error text (that's how iframes fail). Rather than let them think the app
// is broken, we surface a one-tap "Video not playing?" expandable with the two fixes that
// handle >90% of cases: incognito mode and a different browser. This drastically reduces
// "help, videos don't work" support emails from parents on aggressive privacy setups.
function VideoTroubleshootHint() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 text-xs text-ink-soft" data-testid="video-troubleshoot-hint">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="underline hover:text-ink-mid"
        data-testid="video-troubleshoot-toggle"
      >
        Video not playing?
      </button>
      {open && (
        <div className="mt-1.5 text-[11px] leading-relaxed bg-cream border border-edge rounded-lg px-3 py-2" data-testid="video-troubleshoot-body">
          Most of the time this is an ad-blocker or privacy extension blocking the video. Try one of:
          <ol className="list-decimal pl-5 mt-1 space-y-0.5">
            <li>Open the site in a private/incognito window with extensions disabled.</li>
            <li>Try a different browser (Chrome, Safari, Edge).</li>
            <li>If you're on a strict DNS filter or school/work network, try mobile data instead.</li>
          </ol>
        </div>
      )}
    </div>
  );
}

/** Renders any admin-supplied video field as an inline iframe:
 *  - Full <iframe ...> embed HTML (Kajabi share code) → sanitized and rendered as-is
 *  - YouTube / Vimeo / Kajabi URL → converted to the correct embed URL and wrapped in an iframe
 *  - Any other https URL → wrapped in a generic iframe
 *  - Empty → renders the tribe/theme-styled placeholder card so parents see something meaningful
 *
 *  Sanitization: for the iframe-HTML path we accept ONLY a single <iframe> tag and rebuild
 *  it with an allow-listed set of attributes to prevent script injection.
 */

const ALLOWED_IFRAME_ATTRS = new Set([
  "src", "width", "height", "title", "allow", "allowfullscreen",
  "frameborder", "loading", "referrerpolicy",
]);

function sanitizeIframe(html) {
  const match = html.match(/<iframe\b([^>]*)>/i);
  if (!match) return null;
  const attrsRaw = match[1];
  // src is mandatory
  const srcMatch = attrsRaw.match(/\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
  const src = srcMatch ? (srcMatch[1] || srcMatch[2] || srcMatch[3] || "").trim() : "";
  if (!src || !/^https?:\/\//i.test(src)) return null;
  // Optional other attrs we accept
  const attrs = { src, title: "Embedded video", allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen", allowfullscreen: true };
  const attrPattern = /\b([a-zA-Z-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))?/g;
  let m;
  while ((m = attrPattern.exec(attrsRaw)) !== null) {
    const name = m[1].toLowerCase();
    if (!ALLOWED_IFRAME_ATTRS.has(name)) continue;
    const val = m[2] ?? m[3] ?? m[4] ?? "";
    if (name === "src") continue; // already captured & validated
    if (name === "allowfullscreen") { attrs.allowfullscreen = true; continue; }
    attrs[name] = val;
  }
  return attrs;
}

function urlToEmbedSrc(raw) {
  const url = raw.trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    // YouTube
    if (host === "youtu.be") return `https://www.youtube.com/embed${u.pathname}`;
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      if (u.pathname === "/watch") {
        const v = u.searchParams.get("v");
        if (v) return `https://www.youtube.com/embed/${v}`;
      }
      if (u.pathname.startsWith("/embed/")) return url;
      if (u.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
    }
    // Vimeo
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
    // Kajabi / anything else — assume it's already an embeddable URL
    return url;
  } catch { return null; }
}

export default function VideoEmbed({ value, title = "Embedded video", placeholder, className = "", theme, testid = "video-embed" }) {
  const raw = (value || "").trim();

  // 1) Sanitized iframe HTML from admin
  if (raw.toLowerCase().includes("<iframe")) {
    const attrs = sanitizeIframe(raw);
    if (attrs) {
      return (
        <div data-testid={testid}>
          <div className={`aspect-video rounded-2xl overflow-hidden border border-edge bg-black ${className}`}>
            <iframe
              src={attrs.src}
              title={attrs.title || title}
              allow={attrs.allow}
              allowFullScreen={attrs.allowfullscreen || undefined}
              referrerPolicy={attrs.referrerpolicy || "no-referrer-when-downgrade"}
              loading={attrs.loading || "lazy"}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          </div>
          <VideoTroubleshootHint />
        </div>
      );
    }
  }

  // 2) Plain URL → auto-embed
  if (/^https?:\/\//i.test(raw)) {
    const src = urlToEmbedSrc(raw);
    if (src) {
      return (
        <div data-testid={testid}>
          <div className={`aspect-video rounded-2xl overflow-hidden border border-edge bg-black ${className}`}>
            <iframe
              src={src}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              loading="lazy"
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          </div>
          <VideoTroubleshootHint />
        </div>
      );
    }
  }

  // 3) Placeholder — matches previous visual style; consumer can pass a theme block
  const style = theme ? { borderColor: theme.borderColor, background: theme.background, color: theme.color } : {};
  return (
    <div
      className={`aspect-video rounded-2xl border flex items-center justify-center ${theme ? "" : "border-edge bg-cream text-ink-mid"} ${className}`}
      style={style}
      data-testid={`${testid}-placeholder`}
    >
      <div className="text-center px-4">
        <PlayCircle size={36} className="mx-auto opacity-40" />
        <div className="mt-2 text-sm opacity-80">{placeholder || "Video will appear here once your admin adds one"}</div>
      </div>
    </div>
  );
}
